'use client';

import React from 'react';
import Icon from '@/components/ui/AppIcon';

const TransparencySection = () => {
  const allocationData = [
    { category: 'Direct Aid', percentage: 35, color: 'bg-[#8B4513]', description: 'Emergency relief and humanitarian assistance' },
    { category: 'Education', percentage: 30, color: 'bg-[#D2691E]', description: 'Peace education and forgiveness training' },
    { category: 'Community Programs', percentage: 18, color: 'bg-[#FFD700]', description: 'Local chapter support and events' },
    { category: 'Research', percentage: 8, color: 'bg-[#228B22]', description: 'Impact studies and evidence building' },
    { category: 'Operations', percentage: 5, color: 'bg-[#4a90a4]', description: 'Administrative and platform costs' },
    { category: 'Founder Recognition', percentage: 4, color: 'bg-[#9370DB]', description: 'Supports initiative, leadership and full-time dedication to the movement' }
  ];

  const trustIndicators = [
    {
      icon: 'ShieldCheckIcon',
      title: '100% Transparency',
      description: 'Every donation tracked from receipt to delivery with full accountability'
    },
    {
      icon: 'DocumentTextIcon',
      title: 'Annual Reports',
      description: 'Comprehensive financial reports published yearly for public review'
    },
    {
      icon: 'ChartBarIcon',
      title: 'Real-Time Tracking',
      description: 'Monitor your donation impact with live project updates and metrics'
    },
    {
      icon: 'UserGroupIcon',
      title: 'Community Verified',
      description: 'Beneficiaries and local partners validate all program outcomes'
    }
  ];

  return (
    <section className="py-20 bg-background">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-headline font-bold text-foreground mb-4">
            Complete Transparency & Accountability
          </h2>
          <p className="text-lg text-muted-foreground font-body max-w-3xl mx-auto">
            We believe in full transparency. See exactly how your donation is used.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16">
          {/* Fund Allocation Chart */}
          <div className="bg-card rounded-xl p-8 shadow-lg border border-border">
            <h3 className="text-2xl font-headline font-bold text-foreground mb-6 flex items-center space-x-2">
              <Icon name="ChartPieIcon" size={28} className="text-primary" variant="solid" />
              <span>How We Use Your Donation</span>
            </h3>

            <div className="space-y-6">
              {allocationData.map((item, index) => (
                <div key={index}>
                  <div className="flex justify-between items-center mb-2">
                    <div>
                      <span className="font-cta font-bold text-foreground">{item.category}</span>
                      <p className="text-xs text-muted-foreground font-body mt-1">{item.description}</p>
                    </div>
                    <span className="text-2xl font-headline font-bold text-primary">{item.percentage}%</span>
                  </div>
                  <div className="w-full bg-muted rounded-full h-4 overflow-hidden">
                    <div
                      className={`${item.color} h-full rounded-full transition-all duration-1000 ease-out`}
                      style={{ width: `${item.percentage}%` }}
                    ></div>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-6 bg-primary/10 border border-primary/30 rounded-lg p-4">
              <p className="text-sm font-body text-foreground">
                <Icon name="InformationCircleIcon" size={20} className="inline text-primary mr-2" variant="solid" />
                83 cents of every dollar goes directly to programs supporting vulnerable communities (Direct Aid, Education, Community Programs).
              </p>
            </div>
          </div>

          {/* Trust Indicators */}
          <div className="space-y-6">
            {trustIndicators.map((indicator, index) => (
              <div
                key={index}
                className="bg-card rounded-xl p-6 shadow-lg border border-border hover:shadow-xl transition-all duration-300"
              >
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Icon name={indicator.icon as any} size={24} className="text-primary" variant="solid" />
                  </div>
                  <div>
                    <h4 className="text-lg font-cta font-bold text-foreground mb-2">
                      {indicator.title}
                    </h4>
                    <p className="text-sm text-muted-foreground font-body">
                      {indicator.description}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Security Notice */}
        <div className="bg-gradient-to-r from-primary/10 to-secondary/10 rounded-xl p-8 border border-primary/20">
          <div className="flex items-start space-x-4">
            <Icon name="LockClosedIcon" size={32} className="text-primary flex-shrink-0" variant="solid" />
            <div>
              <h3 className="text-xl font-cta font-bold text-foreground mb-2">
                Your Security is Our Priority
              </h3>
              <p className="text-muted-foreground font-body mb-4">
                All donations are processed through secure, encrypted payment gateways. We never store your payment information. 
                Your personal data is protected according to international privacy standards.
              </p>
              <div className="flex flex-wrap gap-4">
                <div className="flex items-center space-x-2">
                  <Icon name="CheckCircleIcon" size={20} className="text-success" variant="solid" />
                  <span className="text-sm font-cta font-bold text-foreground">SSL Encrypted</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Icon name="CheckCircleIcon" size={20} className="text-success" variant="solid" />
                  <span className="text-sm font-cta font-bold text-foreground">PCI Compliant</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Icon name="CheckCircleIcon" size={20} className="text-success" variant="solid" />
                  <span className="text-sm font-cta font-bold text-foreground">GDPR Protected</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TransparencySection;